// Firebase SDK init. Uses emulator host when VITE_USE_EMULATOR=1, otherwise
// production config from VITE_FIREBASE_* env vars.
//
// [BB-fix P0#1] No baked-in fallbacks. The dashboard is open-source friendly
// and we don't want a misconfigured deploy to silently land in our production
// project. Firebase web API keys aren't truly secret (they're public per
// Firebase docs and constrained by Firestore rules), but a hard-coded fallback
// masks misconfigured deployments — fail loud instead.

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

const REQUIRED_KEYS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
];

const env = import.meta.env || {};
const missing = REQUIRED_KEYS.filter((k) => !env[k]);
const isProd = env.PROD === true || env.MODE === 'production' || env.NODE_ENV === 'production';

if (missing.length) {
  // Refuse to boot in production with missing config — silent fall-throughs
  // led to the wrong project being targeted in past deploys (BB-audit P0#1).
  // In dev we still throw so the operator notices, but the message is gentler.
  const msg = `[firebase] missing env vars: ${missing.join(', ')}. Set them in your .env or build pipeline.`;
  if (isProd) {
    throw new Error(`${msg} (production build refuses to start without explicit config)`);
  }
  throw new Error(msg);
}

const config = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
};

export const app = initializeApp(config);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

if (env.VITE_USE_EMULATOR === '1') {
  const host = env.VITE_EMULATOR_HOST || '127.0.0.1';
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, 8080);
  connectStorageEmulator(storage, host, 9199);
  connectFunctionsEmulator(functions, host, 5001);

  console.info('[firebase] emulators connected @', host);
}
